import React, { FC, useEffect, useState, useRef } from "react";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Close, Refresh } from "@mui/icons-material";
import MButton from "components/momentum/MButton";
import { parseWebVtt2, Cookies } from "utils/index";
import {
  TOKEN_COOKIE,
  WEBEX_TOKEN_COOKIE,
  WEBEX_LAST_URL_COOKIE,
  LAST_URL_COOKIE,
} from "constant/index";
import ReactMarkdown from "react-markdown";
import TurndownService from "turndown";
import { toast } from "react-toastify";
// import { getWebexReauthenticateRequired } from "utils/index";
import { useMetadataByCategory } from "components/summary/hooks";

const turndownService = new TurndownService({ codeBlockStyle: "fenced" });

// const WEBEX_REAUTHENTICATE_REQUIRED = getWebexReauthenticateRequired();

const useStyles = makeStyles(() => ({
  titleContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px",
    borderBottom: "1px solid #DEDEDE",
  },
  title: {
    fontSize: 16,
    fontWeight: 500,
    color: "#171B1F",
    lineHeight: "24px",
  },
  content: {
    padding: "16px 24px",
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: "#535759",
    marginBottom: 4,
  },
  filterRow: {
    display: "flex",
    gap: 12,
    alignItems: "flex-end",
  },
  filterField: {
    flex: 1,
  },
  tableHeadCell: {
    fontWeight: 600,
    fontSize: 12,
    color: "#535759",
    backgroundColor: "#F7F7F7",
    padding: "8px 12px",
  },
  tableCell: {
    fontSize: 13,
    padding: "8px 12px",
  },
  tableRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#F0FBFF",
    },
  },
  selectedRow: {
    backgroundColor: "#E6FBFF !important",
  },
  previewLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: "#535759",
    marginBottom: 4,
  },
  actions: {
    padding: "12px 24px",
    borderTop: "1px solid #DEDEDE",
    gap: 8,
    justifyContent: "flex-end",
  },
}));

interface TranscriptItem {
  id: string;
  meetingId: string;
  meetingTopic: string;
  startTime: string;
  vttDownloadLink: string;
  txtDownloadLink: string;
  status: string;
}

const formatLocalDateStr = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toISOWithOffset = (dateStr: string, endOfDay = false): string => {
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? "+" : "-";
  const absOffset = Math.abs(offset);
  const h = Math.floor(absOffset / 60)
    .toString()
    .padStart(2, "0");
  const m = (absOffset % 60).toString().padStart(2, "0");
  const timePart = endOfDay ? "23:59:59" : "00:00:00";
  return `${dateStr}T${timePart}${sign}${h}:${m}`;
};

const getDayDiff = (fromDateStr: string, toDateStr: string): number => {
  const [fromYear, fromMonth, fromDay] = fromDateStr.split("-").map(Number);
  const [toYear, toMonth, toDay] = toDateStr.split("-").map(Number);
  const fromDate = new Date(fromYear, fromMonth - 1, fromDay);
  const toDate = new Date(toYear, toMonth - 1, toDay);
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.floor((toDate.getTime() - fromDate.getTime()) / msPerDay);
};

const buildPreviewText = (
  item: TranscriptItem,
  meetingSummary: any,
  transcriptStr: string
): string => {
  const lines: string[] = [];
  lines.push(`**Webex Meeting: ${item.meetingTopic}**`);
  lines.push("");
  if (item.startTime) {
    lines.push(`*Start Time: ${new Date(item.startTime).toLocaleString()}*`);
  }
  if (meetingSummary?.items?.length) {
    const summary = meetingSummary.items[0];
    if (summary.notes) {
      lines.push("");
      lines.push(
        turndownService.turndown(
          summary.notes.content
            .replaceAll("<h2>", "<h6>")
            .replaceAll("</h2>", "</h6>")
            .replaceAll("<h3>", "<h6>")
            .replaceAll("</h3>", "</h6>")
        )
      );
    }
    if (summary.actionItems?.length) {
      lines.push("");
      lines.push("**Action Items:**");
      lines.push("");
      summary.actionItems.forEach((item: any) => {
        lines.push(`- ${item.content?.trim()}`);
      });
    }
  }
  if (transcriptStr.trim()) {
    lines.push("");
    lines.push("**Transcript:**");
    lines.push("");
    lines.push(transcriptStr);
  }
  return lines.join("\n");
};

export interface MeetingNoteProps {
  open: boolean;
  onClose: () => void;
  onReplace: (text: string) => void;
  onInsert: (text: string) => void;
}

const MeetingNote: FC<MeetingNoteProps> = ({ open, onClose, onReplace, onInsert }) => {
  const classes = useStyles();

  const getDefaults = () => {
    const now = new Date();
    const prev = new Date(now);
    prev.setDate(now.getDate() - 6);
    return {
      toDate: formatLocalDateStr(now),
      fromDate: formatLocalDateStr(prev),
    };
  };

  // const [token, setToken] = useState("");
  const tokenRef = useRef("");
  const [siteUrl, setSiteUrl] = useState("cisco.webex.com");
  const [from, setFrom] = useState(() => getDefaults().fromDate);
  const [to, setTo] = useState(() => getDefaults().toDate);
  const [transcripts, setTranscripts] = useState<TranscriptItem[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [preview, setPreview] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewText, setPreviewText] = useState("");
  const [reAuthConfirmOpen, setReAuthConfirmOpen] = useState(false);
  const [reAuthUrl, setReAuthUrl] = useState("");

  const { data: webexConfigs } = useMetadataByCategory("WEBEX_CONFIG") || [];

  const webex_reauthenticate_required =
    webexConfigs?.find((c) => c.code === "REAUTHENTICATE_REQUIRED")?.value === "true";

  useEffect(() => {
    if (!open) return;
    const { fromDate, toDate } = getDefaults();
    setSiteUrl("cisco.webex.com");
    setFrom(fromDate);
    setTo(toDate);
    setTranscripts([]);
    setListError("");
    setSelectedId(null);
    setPreview("");
    setPreviewText("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reAuthenticateIfNeeded = () => {
    if (webex_reauthenticate_required) {
      const cookieWebexToken = Cookies.get(WEBEX_TOKEN_COOKIE);
      if (!cookieWebexToken) {
        Cookies.set(WEBEX_LAST_URL_COOKIE, location.pathname + location.search);
        setReAuthUrl("/spng/webex-auth");
        setReAuthConfirmOpen(true);
        return;
      }
      tokenRef.current = cookieWebexToken;
    } else {
      const cookieToken = Cookies.get(TOKEN_COOKIE);
      if (!cookieToken) {
        Cookies.set(LAST_URL_COOKIE, location.pathname + location.search);
        window.location.assign("/spng");
        return;
      }
      tokenRef.current = cookieToken;
    }
  };

  const handleReAuthConfirm = () => {
    setReAuthConfirmOpen(false);
    window.location.assign(reAuthUrl);
  };

  const handleFetch = async () => {
    setListLoading(true);
    reAuthenticateIfNeeded();
    setListLoading(true);
    reAuthenticateIfNeeded();
    setListLoading(true);
    setListError("");
    setTranscripts([]);
    setSelectedId(null);
    setPreview("");
    setPreviewText("");
    try {
      if (!from || !to) {
        setListError("Please select a valid date range.");
        return;
      }

      const dayDiff = getDayDiff(from, to);
      if (dayDiff < 0) {
        setListError("'To' date cannot be earlier than 'From' date.");
        return;
      }

      // Keep the query window within 30 days for API limits.
      if (dayDiff > 30) {
        setListError("Date range cannot exceed 30 days.");
        return;
      }

      const params = new URLSearchParams({
        siteUrl,
        from: toISOWithOffset(from),
        to: toISOWithOffset(to, true),
      });
      const token = tokenRef.current;
      if (!token) {
        setListError("Authentication token is missing. Please re-authenticate.");
        return;
      }
      const res = await fetch(`/api/webex/meeting/transcripts?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.status === 401) {
        if (webex_reauthenticate_required) {
          Cookies.set(WEBEX_LAST_URL_COOKIE, location.pathname + location.search);
          toast.error("Unauthorized. Please log in to re-authenticate.", {
            position: "top-center",
            autoClose: 2000,
          });
          window.location.assign("/spng/webex-auth");
        } else {
          Cookies.set(LAST_URL_COOKIE, location.pathname + location.search);
          toast.error("Unauthorized. Please log in to re-authenticate.", {
            position: "top-center",
            autoClose: 2000,
          });
          window.location.assign("/spng");
        }
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error ?? `Request failed (${res.status})`);
      }
      const data = await res.json();
      const items: TranscriptItem[] = data?.items ?? [];
      setTranscripts(items);
      if (!items.length) setListError("No transcripts found for the selected date range.");
    } catch (e: any) {
      setListError(e?.message ?? "Failed to fetch transcripts.");
    } finally {
      setListLoading(false);
    }
  };

  const handleSelectItem = async (item: TranscriptItem) => {
    setSelectedId(item.id);
    setPreviewLoading(true);
    setPreview("");
    setPreviewText("");
    const token = tokenRef.current;
    try {
      const params = new URLSearchParams({ url: item.vttDownloadLink });
      const [transcriptRes, meetingSummaryRes] = await Promise.all([
        fetch(`/api/webex/meeting/fetchTranscript?${params}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(`/api/webex/meeting/fetchMeetingSummary?meetingId=${item.meetingId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      let transcriptStr = "";
      if (transcriptRes.ok) {
        const vttText = await transcriptRes.text();
        try {
          const cues = parseWebVtt2(vttText);
          transcriptStr = cues
            .sort((a, b) => a.id - b.id)
            .map(
              (c) => `${c.speaker ? `**[${c.start.split(".")[0]}] ${c.speaker}:** ` : ""} ${c.text}`
            )
            .join("\n\n");
        } catch {
          transcriptStr = vttText;
        }
      }
      if (!meetingSummaryRes.ok) {
        const err = await meetingSummaryRes.json().catch(() => ({}));
        throw new Error(err?.error ?? `Request failed (${meetingSummaryRes.status})`);
      }

      const meetingSummaryData = await meetingSummaryRes.json();
      const full = buildPreviewText(item, meetingSummaryData, transcriptStr);
      setPreviewText(full);
      setPreview(full);
    } catch {
      setPreview("Failed to load transcript.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleReplace = () => {
    if (previewText) onReplace(previewText);
  };

  const handleInsert = () => {
    if (previewText) onInsert(previewText);
  };

  const listMaxHeight = transcripts.length > 5 ? 210 : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      {/* Title bar */}
      <div className={classes.titleContainer}>
        <Typography className={classes.title}>Add Webex Meeting Summary and Transcript</Typography>
        <IconButton
          size="small"
          onClick={onClose}
        >
          <Close fontSize="small" />
        </IconButton>
      </div>

      <DialogContent className={classes.content}>
        {/* Site URL / From / To / Fetch */}
        <div className={classes.filterRow}>
          <div className={classes.filterField}>
            <Typography className={classes.fieldLabel}>Site URL</Typography>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={siteUrl}
              onChange={(e) => setSiteUrl(e.target.value)}
              inputProps={{ spellCheck: false }}
            />
          </div>
          <div className={classes.filterField}>
            <Typography className={classes.fieldLabel}>From</Typography>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              inputProps={{ max: to }}
            />
          </div>
          <div className={classes.filterField}>
            <Typography className={classes.fieldLabel}>To</Typography>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              inputProps={{ min: from }}
            />
          </div>
          <MButton
            buttonType="blue"
            disabled={listLoading}
            onClick={handleFetch}
          >
            {listLoading ? "Loading…" : "Fetch"}
          </MButton>
        </div>

        {/* Transcript List */}
        {(transcripts.length > 0 || listError) && (
          <div>
            <Typography className={classes.fieldLabel}>Transcript List</Typography>
            {listError ? (
              <Typography style={{ fontSize: 13, color: "#d32f2f" }}>{listError}</Typography>
            ) : (
              <TableContainer
                component={Paper}
                variant="outlined"
                style={{
                  maxHeight: listMaxHeight,
                  ...(listMaxHeight ? { overflowY: "auto" } : {}),
                }}
              >
                <Table
                  size="small"
                  stickyHeader
                >
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeadCell}>Meeting Topic</TableCell>
                      <TableCell
                        className={classes.tableHeadCell}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        Start Time
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transcripts.map((item) => (
                      <TableRow
                        key={item.id}
                        className={`${classes.tableRow} ${
                          item.id === selectedId ? classes.selectedRow : ""
                        }`}
                        onClick={() => handleSelectItem(item)}
                      >
                        <TableCell className={classes.tableCell}>{item.meetingTopic}</TableCell>
                        <TableCell
                          className={classes.tableCell}
                          style={{ whiteSpace: "nowrap" }}
                        >
                          {item.startTime ? new Date(item.startTime).toLocaleString() : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </div>
        )}

        {/* Preview */}
        {selectedId && (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                marginBottom: 4,
              }}
            >
              <Typography
                className={classes.previewLabel}
                style={{ marginBottom: 0 }}
              >
                Preview
              </Typography>
              <Tooltip title="Reload preview">
                <span>
                  <IconButton
                    size="small"
                    disabled={previewLoading}
                    onClick={() => {
                      const item = transcripts.find((t) => t.id === selectedId);
                      if (item) handleSelectItem(item);
                    }}
                  >
                    <Refresh style={{ fontSize: 15 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
            <div style={{ position: "relative" }}>
              <div
                style={{
                  minHeight: 80,
                  maxHeight: 280,
                  overflowY: "auto",
                  backgroundColor: "#F7F7F7",
                  border: "1px solid rgba(0,0,0,0.23)",
                  borderRadius: 4,
                  padding: "8px 12px",
                  fontSize: 13,
                  color: preview ? "inherit" : "#9e9e9e",
                }}
              >
                {preview ? (
                  <div
                    className="vditor-reset"
                    style={{ fontSize: 13 }}
                  >
                    <ReactMarkdown>{preview}</ReactMarkdown>
                  </div>
                ) : (
                  !previewLoading && "Preview will appear here"
                )}
              </div>
              {previewLoading && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    backgroundColor: "rgba(247,247,247,0.75)",
                    borderRadius: 4,
                    pointerEvents: "none",
                  }}
                >
                  <CircularProgress
                    size={16}
                    thickness={5}
                  />
                  <Typography style={{ fontSize: 12, color: "#535759" }}>
                    Loading transcript…
                  </Typography>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <MButton onClick={onClose}>Cancel</MButton>
        <MButton
          buttonType="blue-reverse"
          disabled={!previewText}
          onClick={handleReplace}
        >
          Replace Note
        </MButton>
        <MButton
          buttonType="blue"
          disabled={!previewText}
          onClick={handleInsert}
        >
          Insert Note
        </MButton>
      </DialogActions>

      {/* Re-authentication confirmation dialog */}
      <Dialog
        open={reAuthConfirmOpen}
        onClose={() => setReAuthConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <div className={classes.titleContainer}>
          <Typography className={classes.title}>Re-authentication Required</Typography>
          <IconButton
            size="small"
            onClick={() => setReAuthConfirmOpen(false)}
          >
            <Close fontSize="small" />
          </IconButton>
        </div>
        <DialogContent className={classes.content}>
          <Typography style={{ fontSize: 14, color: "#535759" }}>
            Need authentication for your Webex meeting info. Do you want to continue?
          </Typography>
          <Typography style={{ fontSize: 12, color: "#9e9e9e" }}>
            <strong>Reminder:</strong> if you choose to continue, you will be redirected to the
            login page, so please save your draft note first accordingly. After successful login,
            please come back to this page to fetch your meeting info again.
          </Typography>
        </DialogContent>
        <DialogActions className={classes.actions}>
          <MButton onClick={() => setReAuthConfirmOpen(false)}>Cancel</MButton>
          <MButton
            buttonType="blue"
            onClick={handleReAuthConfirm}
          >
            Continue
          </MButton>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default MeetingNote;
